import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "bob_admin_lang";
const AdminI18nContext = createContext(null);

const translations = {
  en: {
    shell: {
      main: "Main",
      operations: "Operations",
      system: "System",
      dashboard: "Dashboard",
      orders: "Orders",
      products: "Products",
      customers: "Customers",
      shipping: "Shipping Rates",
      shippingOnly: "Shipping Only",
      warehouses: "Warehouses",
      content: "Content",
      settings: "Settings",
      approvals: "Approvals",
      notifications: "Notifications",
      console: "Operations Console",
      signedIn: "Signed in",
      secureAccess: "Secure access to orders, catalog, shipping, and CMS settings.",
      password: "Password",
      signOut: "Sign out",
      openNav: "Open admin navigation",
      closeNav: "Close admin navigation",
      closeOverlay: "Close admin navigation overlay",
      mobilePinned: "Navigation stays pinned inside the viewport on mobile.",
      language: "Language",
      english: "English",
      arabic: "العربية",
      adminUser: "Admin user",
    },
    login: {
      title: "BOB Admin",
      subtitle: "Internal operations access only",
      intro:
        "Sign in to manage orders, catalog, customers, warehouses, and content. Public commerce flows stay unchanged.",
      email: "Email",
      password: "Password",
      signIn: "Sign in",
      signingIn: "Signing in...",
      processing: "Processing...",
      restricted: "BOB Services LB - restricted access",
      loginFailed: "Login failed",
      emailPlaceholder: "",
      passwordPlaceholder: "",
      forgotPassword: "Forgot password?",
      forgotTitle: "Reset admin access",
      forgotSubtitle: "Manager code verification",
      forgotIntro: "Enter the admin email. If eligible, a reset code is sent to the manager inbox.",
      sendResetCode: "Send reset code",
      resetRequestFailed: "Could not request a reset code",
      resetCodeSent: "If the account is eligible, a reset code was sent.",
      resetTitle: "Enter reset code",
      resetSubtitle: "Code sent to Ibrahim.kobaisy@gmail.com",
      resetIntro: "Use the manager code to set a new admin password.",
      resetCode: "Reset code",
      newPassword: "New password",
      resetPassword: "Reset password",
      resetFailed: "Could not reset password",
      resetComplete: "Password reset. Sign in with the new password.",
      backToLogin: "Back to login",
      resendCode: "Resend code",
    },
    dashboard: {
      title: "Dashboard",
      description:
        "Work across orders, stock, products, analytics, payments, and admin logs without changing any backend workflows.",
      overview: "Admin overview",
      overviewTitle: "Centralize commerce, content, and operations in one dashboard.",
      overviewBody:
        "The dashboard keeps the existing order, stock, product, payment, and audit tools intact. This pass restructures the shell, hierarchy, and mobile behavior so the same actions are easier to run safely.",
      shippingRates: "Shipping rates",
      shippingRatesBody: "Edit global fees, live rate rows, and calculator inputs.",
      content: "Content",
      contentBody: "Update hero copy, banners, how-it-works steps, and announcements.",
      ordersQueue: "Orders queue",
      ordersQueueBody: "Review incoming orders, update statuses, add notes, and export XLSX files.",
      ordersQueueAction: "Open orders",
      catalogControl: "Catalog control",
      catalogControlBody: "Manage stock overlays, product cards, bulk uploads, and missing data checks.",
      catalogControlAction: "Open products",
      customers: "Customers",
      products: "Products",
      customersBody: "Switch to the registered customers area for subscriber records and resets.",
      customersAction: "Open customers",
      shippingOps: "Shipping ops",
      shippingOpsBody: "Edit public rates, Shipping Only entries, and warehouse address cards.",
      shippingOpsAction: "Open shipping",
      workspace: "Admin workspace",
      workspaceNote: "Orders, products, stock, payments, logs, and analytics remain on the existing /admin route.",
      orders: "Orders",
      orderCustomers: "Order Customers",
      trash: "Trash",
      stock: "Stock",
      shein: "SHEIN",
      missingData: "Missing Data",
      payments: "Payments",
      logs: "Logs",
      analytics: "Analytics",
      allStatuses: "All statuses",
      exportXlsx: "Export XLSX",
      refresh: "Refresh",
      noOrdersYet: "No orders yet",
      ordersHeading: "Orders",
      ordersBody: "Keep fulfilment moving with status updates, exports, customer notes, and clear payment totals.",
      customer: "Customer",
      paymentTotals: "Payment and totals",
      items: "Items",
      setStatus: "Set status:",
      moveToTrash: "Move to Trash",
      cancellationReason: "Cancellation reason",
      searchByIdOrName: "Search by #ID or name",
      noStockRecords: "No stock records",
      single: "Single",
      customerSearchPlaceholder: "Search by customer ID, phone, email, or name...",
      noCustomerMatch: "No match for \"{query}\"",
      noCustomersYet: "No customers yet",
      paymentMethods: "Payment Methods",
      paymentMethodsBody: "Toggle which methods customers see at checkout. Changes apply immediately to the website, mobile app, and order email.",
      paymentLoadFailed: "Could not load payment methods",
      paymentAtLeastOne: "At least one payment method must remain enabled.",
      enabled: "Enabled",
      disabled: "Disabled",
      saveFailed: "Save failed",
      adminLogs: "Admin Logs",
      adminLogsBody: "Every important admin action is recorded here automatically (append-only). Use the filters to find specific changes.",
      allKinds: "All kinds",
      allSources: "All sources",
      allSeverity: "All severity",
      allResults: "All results",
      logsActionPlaceholder: "Filter by action (e.g. order.status, product.update)...",
      noLogsYet: "No logs yet",
      before: "before",
      after: "after",
      details: "Details",
      on: "on",
      by: "by",
      sheinCategories: "SHEIN Categories",
      addCategory: "Add category",
      sheinCategoriesBody: "Drag rows to reorder. Hidden categories disappear from the storefront / mobile app but stay attached to existing products.",
      noCategoriesYet: "No categories yet",
      sheinLoadFailed: "Could not load SHEIN categories",
      sheinDeleteConfirm: "Delete SHEIN category \"{name}\"? Products already in this category will keep their tag, but the chip disappears from the storefront.",
      updated: "Updated",
      created: "Created",
      deleted: "Deleted",
      deleteFailed: "Delete failed",
      orderSaved: "Order saved",
      reorderFailed: "Reorder failed - refresh",
      show: "Show",
      hide: "Hide",
      edit: "Edit",
      delete: "Delete",
      cancel: "Cancel",
      save: "Save",
      create: "Create",
      englishNameRequired: "English name is required",
      editNamed: "Edit \"{name}\"",
      newSheinCategory: "New SHEIN category",
      idSlugOptional: "ID (slug) - optional, auto-derived from EN name",
      nameEn: "Name EN *",
      nameAr: "Name AR",
      nameFr: "Name FR",
    },
    analytics: {
      title: "Analytics",
      description: "Every metric below separates website, mobile app, unknown, and total values.",
      notAvailable: "No analytics available",
      today: "Today",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      allTime: "All time",
      lastDays: "Last {days} days",
      all: "All",
      daysShort: "{days}d",
      orders: "orders",
      revenue: "revenue",
      avg: "avg",
      pageViews: "Page views",
      uniqueSessions: "Unique sessions",
      whatsappClicks: "WhatsApp clicks",
      searches: "Searches",
      addToCart: "Add to cart",
      failedCount: "{count} failed",
      checkoutStarts: "Checkout starts",
      successCount: "{count} success",
      sourceTitle: "Website / app source split",
      sourceDescription: "Total always equals website + mobile app + unknown; historical rows without a source remain visible as unknown.",
      website: "Website",
      app: "App",
      mobileApp: "Mobile app",
      unknown: "Unknown",
      combined: "Combined",
      total: "Total",
      appNotConnected: "App analytics are not connected yet.",
      visits: "Visits",
      events: "Events",
      cartEvents: "Cart events",
      checkoutEvents: "Checkout events",
      warehouseViews: "Warehouse views",
      shippingCalculatorViews: "Shipping calculator views",
      authEvents: "Login / signup events",
      externalEvents: "External app rows",
      customers: "Customers",
      registered: "Registered",
      verified: "Verified",
      unverified: "Unverified",
      withOrders: "With orders",
      newIn30Days: "New in 30 days",
      missingProfileData: "Missing profile data",
      products: "Products",
      staticCatalog: "Static catalog",
      dbProducts: "DB products",
      overrideRows: "Override rows",
      hidden: "Hidden",
      outOfStock: "Out of stock",
      orderRowsMissingIdentity: "Order rows missing identity",
      shipping: "Shipping",
      rates: "Rates",
      enabledRates: "Enabled rates",
      rateCountries: "Rate countries",
      rateMethods: "Rate methods",
      shippingOnlyEntries: "Shipping Only entries",
      warehouses: "Warehouses",
      trackingHealth: "Tracking health",
      trackingNotAvailable: "Tracking data not available yet.",
      rangeEvents: "Range events",
      allTimeEvents: "All-time events",
      lastEvent: "Last event",
      range: "Range",
      collectionsFound: "Collections found",
      traffic: "Traffic",
      noPageViews: "No page views yet",
      languages: "Languages",
      noLanguageData: "No language data",
      devices: "Devices",
      noDeviceData: "No device data",
      countryPages: "Country pages",
      noCountryPageViews: "No country page views yet",
      productInterest: "Product interest",
      viewed: "Viewed",
      failedAdds: "Failed adds",
      topViewedProducts: "Top viewed products",
      noProductViews: "No product views yet",
      funnel: "Funnel",
      productViews: "Product views",
      cartViews: "Cart views",
      checkoutSuccess: "Checkout success",
      checkoutFailed: "Checkout failed",
      missingStepData: "Missing step data",
      totalClicks: "Total clicks",
      topPages: "Top pages",
      noWhatsappClicks: "No WhatsApp clicks yet",
      byCountry: "By country",
      noCountryMetadata: "No country metadata yet",
      shippingTools: "Shipping tools",
      calculatorViews: "Calculator views",
      calculations: "Calculations",
      countrySelections: "Country selections",
      methodSelections: "Method selections",
      shippingOnlyOpens: "Shipping Only opens",
      addressCopies: "Address copies",
      shippingMarkCopies: "Shipping mark copies",
      topMethods: "Top methods",
      noMethodSelections: "No method selections yet",
      chinaSuppliersAuth: "China suppliers + auth",
      chinaSuppliers: "China suppliers",
      sectionViews: "Section views",
      openClicks: "Open clicks",
      customerAuth: "Customer auth",
      loginSuccess: "Login success",
      loginFailed: "Login failed",
      signupSuccess: "Signup success",
      signupFailed: "Signup failed",
      verificationSent: "Verification sent",
      verificationSuccess: "Verification success",
      passwordResetRequests: "Password reset requests",
      recentEvents: "Recent events",
      noTrackedEvents: "No tracked events yet",
      source: "Source",
      byStatusLifetime: "By status (lifetime)",
      noOrders: "No orders",
      topProducts30d: "Top products (30d)",
      noDataYet: "No data yet",
      units: "units",
      topCustomers: "Top customers",
      noCustomerOrderData: "No customer order data yet",
      recentOrders: "Recent orders",
      noRecentOrders: "No recent orders",
      recentAdminLogs: "Recent admin logs",
      noAdminLogs: "No admin logs yet",
      totalLoggedActions: "Total logged admin actions",
    },
    customers: {
      title: "Customers",
      description:
        "Review registered customer records, account identity, verification state, and reset-email actions without exposing secrets.",
      registeredEyebrow: "Registered customers",
      subscribers: "Subscribers",
      intro:
        "Search, inspect, suspend, and send secure reset links. Customer IDs and full identity fields come from the backend record when available.",
      total: "Total",
      active: "Active",
      all: "All",
      inactive: "Inactive",
      suspended: "Suspended",
      frozen: "Frozen",
      deleted: "Deleted",
      closed: "Closed",
      restrictedAccounts: "Frozen / suspended",
      searchPlaceholder: "Search by customer ID, name, email, or phone...",
      refresh: "Refresh",
      loadingCustomers: "Loading customers...",
      noCustomers: "No customers match your filters.",
      customerId: "Customer ID",
      fullName: "Full name",
      email: "Email",
      phone: "Phone",
      verification: "Verification",
      verified: "Verified",
      unverified: "Unverified",
      notAvailable: "Not available",
      status: "Status",
      orders: "Orders",
      totalSpent: "Total spent",
      registered: "Registered",
      customer: "Customer",
      date: "Date",
      totalLabel: "Total",
      payment: "Payment",
      detailTitle: "Customer",
      language: "Language",
      address: "Address",
      city: "City",
      country: "Country",
      deliveryNotes: "Delivery notes",
      lastLogin: "Last login",
      lastOrder: "Last order",
      orderCount: "Order count",
      adminActions: "Admin actions",
      reactivate: "Reactivate",
      freeze: "Freeze",
      suspend: "Suspend",
      markInactive: "Mark inactive",
      activate: "Active",
      deleteConfirm: "Delete this customer account? Orders are kept.",
      deleteSuccess: "Customer account deleted",
      sendResetEmail: "Send password reset email",
      sendResetPrompt: "Send reset link to {email}?",
      yesSend: "Yes, send",
      cancel: "Cancel",
      resetNotice: "Reset links are single-use, expire in 60 minutes, and are sent by email. The token is never shown in admin.",
      recentOrders: "Recent orders",
      lastLoginNote: "This API does not currently return a last-login audit field for every customer record.",
      noOrdersLinked: "No orders linked to this customer.",
      deletionRequests: "Account deletion requests",
      deletionQueue: "Pending review queue",
      deletionIntro:
        "Review customer account closure requests here. Delete closes the old account while preserving order history, and reject keeps the account active. Freeze and unfreeze are managed independently from customer details.",
      loadingDeletionRequests: "Loading deletion requests...",
      noDeletionRequests: "No account deletion requests found.",
      reason: "Reason",
      reasonOther: "Other",
      requested: "Requested",
      note: "Note",
      adminDecisionNote: "Admin decision note",
      rejectionReason: "Rejection reason",
      rejectionReasonPlaceholder: "Required when rejecting",
      rejectionReasonRequired: "Please enter a rejection reason before rejecting the request.",
      markReviewed: "Mark reviewed",
      markCompleted: "Mark completed",
      deleteAccountAction: "Delete account",
      freezeAccountAction: "Freeze account",
      unfreezeAccountAction: "Unfreeze account",
      reject: "Reject",
      internalId: "Internal ID",
      deletionSections: {
        pending: "Pending review",
        frozen: "Frozen accounts",
        completed: "Deleted accounts",
        rejected: "Rejected requests",
      },
      notifications: "App notifications",
      verifiedSendControl: "Verified send control",
      accountSource: "Account source",
      sourceCustomer: "Customer",
      sourceLegacyUser: "Legacy user",
      sourceUnknown: "Unknown",
      deletionStatusUpdated: "Deletion request updated",
      accountRemainsFrozen: "Request rejected, but the account remains frozen because it was frozen for another reason.",
      verificationCodeSent: "Verification code sent to {email}",
      pushIntro: "Prepare a notification, request a manager verification code by email, then attempt the final send. This build does not fake delivery if mobile push tokens or provider credentials are missing.",
      pushWarning: "This message will be sent to mobile app users. Confirm the content carefully before sending.",
      pushWarningAr: "تنبيه مهم: هذه الرسالة ستصل إلى مستخدمي التطبيق. تأكد جيدًا من محتوى الرسالة قبل الإرسال.",
      notificationTitle: "Notification title",
      notificationBody: "Notification body",
      audience: "Audience",
      allUsers: "All users",
      verificationCode: "Verification code",
      requestVerificationCode: "Request verification code",
      verifyAndSend: "Verify and send",
      preview: "Preview",
      notificationTitleFallback: "Notification title",
      notificationBodyFallback: "Your notification preview will appear here before you request the verification code.",
      recentAttempts: "Recent attempts",
      recentAttemptsBody: "Every code request and send attempt is logged here.",
      loadingNotificationLogs: "Loading notification logs...",
      noNotificationAttempts: "No notification attempts logged yet.",
      provider: "Provider",
      loadingCustomer: "Loading customer...",
      verifiedAt: "Verified at",
      requestStatus: {
        pending: "pending",
        reviewed: "reviewed",
        completed: "completed",
        frozen: "frozen",
        deleted: "deleted",
        rejected: "rejected",
        cancelled: "cancelled",
        failed: "failed",
        code_requested: "code requested",
        draft: "draft",
      },
    },
    shipping: {
      title: "Shipping Rates",
      description:
        "Manage global delivery fees, exchange rate hints, and the live country-by-method rate matrix used across customer-facing shipping surfaces.",
      globalFees: "Global fees",
      deliveryFee: "Delivery fee (USD)",
      exchangeRate: "USD to LBP exchange rate",
      deliveryFeeHint: "Charged on every BOB Store order",
      exchangeRateHint: "Used for LBP price hints",
      tableTitle: "Shipping rates by country and method",
      addRate: "Add rate",
      noRates: "No rates yet. Click Add rate to create one.",
      saveChanges: "Save changes",
      country: "Country",
      method: "Method",
      productCategory: "Product category",
      price: "Price",
      unit: "Unit",
      combo: "+ Combo",
      eta: "ETA",
      active: "Active",
      id: "ID",
    },
    shippingOnly: {
      title: "Shipping Only",
      description:
        "Edit service copy, SHEIN instructions, and the dedicated Shipping Only pricing list used on the website and mobile app.",
      about: "About this section",
      aboutBody:
        "Shipping Only is a separate service from the country-to-Lebanon calculator. Edits here only affect the public /shipping-only page and the mobile app.",
      prices: "Prices",
      pageTexts: "Page Texts",
      sheinInstructions: "SHEIN Instructions",
      warehousesLink: "Warehouses",
      addPrice: "Add price",
      saveChanges: "Save changes",
      loading: "Loading...",
      publicPageTexts: "Public page texts",
      warehousesIntro:
        "Warehouse addresses, phone numbers, shipping marks, and notes are managed in their own section. Edits there reflect automatically on the public /shipping-only page.",
      openWarehouses: "Open Warehouses",
    },
    warehouses: {
      title: "Warehouses",
      description:
        "Maintain every warehouse card, contact line, shipping mark, and address block that feeds the website, mobile app, and Shipping Only pages.",
      subtitle:
        "Single source of truth - mirrors the /shipping-only page, the website, and the mobile app.",
      newWarehouse: "New warehouse",
      noWarehouses: "No warehouses yet. Click New warehouse to add one.",
      active: "Active",
      hidden: "Hidden",
      edit: "Edit",
      create: "Create",
      save: "Save",
      cancel: "Cancel",
      idSlug: "ID (slug)",
      city: "City",
      shippingMethod: "Shipping method",
      deliveryEta: "Delivery ETA",
      shippingMark: "Shipping mark",
      warehouseCode: "Warehouse code (internal)",
      sortOrder: "Sort order",
      mapUrl: "Map URL (optional)",
      email: "Email",
      whatsapp: "WhatsApp (warehouse-specific)",
      contactLabel: "Contact person / label",
      chineseAddress: "Chinese address",
      phoneNumbers: "Phone numbers",
      addPhone: "+ Add",
      noPhoneNumbers: "No phone numbers yet.",
    },
    content: {
      title: "Content",
      description:
        "Manage connected website/mobile content feed rows. Product, order, warehouse, shipping, and account settings stay in their dedicated sections.",
      heroEntries: "Hero entries",
      banners: "Banners",
      steps: "\"How it works\" steps",
      announcements: "Announcements",
      addHero: "Add hero",
      addBanner: "Add banner",
      addStep: "Add step",
      addAnnouncement: "Add announcement",
      saveAll: "Save all",
      loading: "Loading...",
      failedToLoad: "Failed to load content",
      saved: "Content saved",
      saveFailed: "Save failed",
      heroHint: "Create one entry per platform variant - tag each as Web / Mobile / Both.",
      advancedFeedTitle: "Advanced content feed",
      advancedFeedBody: "Homepage feed controls are hidden because no live rows are configured. Shipping, warehouse, product, and account settings remain in their dedicated admin sections. Open this only when you need to create website/mobile feed rows.",
      showFeedControls: "Show feed controls",
      heroItem: "Hero entry",
      bannerItem: "Banner",
      stepItem: "Step",
      announcementItem: "Announcement",
      titleLabel: "Title",
      subtitleLabel: "Subtitle",
      descriptionLabel: "Description",
      textLabel: "Text",
      iconName: "Icon name",
      visible: "Visible",
      image: "Image",
      linkUrl: "Link URL",
      type: "Type",
      noHeroEntries: "No hero entries yet.",
      noBanners: "No banners yet.",
      noSteps: "No steps yet.",
      noAnnouncements: "No announcements yet.",
      workflow: "Draft / Preview / Publish",
      status: "Status",
      statuses: {
        draft: "Draft",
        published: "Published",
      },
      lastSaved: "Last saved",
      notSaved: "not saved",
      lastPublished: "Last published",
      notPublished: "not published",
      appNotConnected: "App content delivery is not connected yet.",
      all: "All",
      website: "Website",
      app: "App",
      preview: "Preview",
      publish: "Publish",
      hero: "Hero",
      banner: "Banner",
      step: "Step",
      announcement: "Announcement",
      untitled: "Untitled",
      noPreviewItems: "No preview items for this surface.",
      draftSaved: "Draft saved",
      publishFailed: "Publish failed",
      published: "Content published",
      saveDraft: "Save draft",
    },
    settings: {
      title: "Settings",
      description: "Manage admin roles, source separation, login security, and account controls for the website admin.",
      accountControls: "Account controls",
      changePasswordHint: "Change your own admin password.",
      role: "Role",
      pendingApprovals: "Pending approvals",
      unreadNotifications: "Unread notifications",
      lockedLogins: "Locked logins",
      sourcePolicy: "Source and sync policy",
      websiteCustomers: "Website customers",
      mobileApp: "Mobile app",
      contentWorkflow: "Content workflow",
      customerIds: "Customer IDs",
      employeeAccounts: "Employee/admin accounts",
      employeeDomainHint: "Employee emails must end with @bobserviceslb.com.",
      name: "Name",
      email: "Email",
      temporaryPassword: "Temporary password",
      createAccount: "Create account",
      currentStaff: "Current staff",
      resetPassword: "Reset password",
      rolePermissions: "Role permissions",
      permissions: "Permissions",
      presets: "Presets",
      pendingApprovalsWarning: "{count} approval request(s) need review.",
      lockedLoginsWarning: "{count} login account(s) are temporarily locked.",
    },
    approvals: {
      title: "Approvals",
      description: "Review controlled admin changes before they are treated as approved operational requests.",
      newRequest: "New request",
      requestTitle: "Title",
      area: "Area",
      targetId: "Target ID",
      requestDescription: "Description",
      createRequest: "Create request",
      requests: "Requests",
      loading: "Loading approvals...",
      empty: "No approval requests yet.",
      requestedBy: "Requested by",
      reviewedBy: "Reviewed by",
      reviewedAt: "Reviewed at",
      note: "Note",
      approve: "Approve",
      reject: "Reject",
      created: "Approval request created",
      approved: "Approval approved",
      rejected: "Approval rejected",
      loadFailed: "Could not load approvals",
      createFailed: "Create failed",
      decisionFailed: "Decision failed",
      decisionNotePrompt: "Optional decision note",
    },
    notifications: {
      title: "Notifications",
      description: "Security, approval, content, and system notices generated by website admin activity.",
      all: "All",
      unreadCount: "Unread ({count})",
      readAll: "Read all",
      loading: "Loading notifications...",
      empty: "No notifications yet.",
      markRead: "Mark read",
      markedRead: "Notifications marked read",
      loadFailed: "Could not load notifications",
      markFailed: "Could not mark as read",
      markAllFailed: "Could not mark all as read",
    },
  },
  ar: {
    shell: {
      main: "الرئيسية",
      operations: "العمليات",
      system: "النظام",
      dashboard: "لوحة التحكم",
      orders: "الطلبات",
      products: "المنتجات",
      customers: "العملاء",
      shipping: "أسعار الشحن",
      shippingOnly: "الشحن فقط",
      warehouses: "المستودعات",
      content: "المحتوى",
      settings: "الإعدادات",
      approvals: "الموافقات",
      notifications: "الإشعارات",
      console: "لوحة العمليات",
      signedIn: "تم تسجيل الدخول",
      secureAccess: "وصول آمن إلى الطلبات والكتالوج والشحن وإعدادات إدارة المحتوى.",
      password: "كلمة المرور",
      signOut: "تسجيل الخروج",
      openNav: "فتح تنقل الإدارة",
      closeNav: "إغلاق تنقل الإدارة",
      closeOverlay: "إغلاق طبقة تنقل الإدارة",
      mobilePinned: "يبقى التنقل مثبتاً داخل الشاشة على الجوال.",
      language: "اللغة",
      english: "English",
      arabic: "العربية",
      adminUser: "مستخدم الإدارة",
    },
    login: {
      title: "إدارة BOB",
      subtitle: "وصول داخلي للعمليات فقط",
      intro:
        "سجّل الدخول لإدارة الطلبات والكتالوج والعملاء والمستودعات والمحتوى، مع بقاء تدفقات التجارة العامة دون تغيير.",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      signIn: "تسجيل الدخول",
      signingIn: "جارٍ تسجيل الدخول...",
      restricted: "BOB Services LB - وصول مقيّد",
      loginFailed: "فشل تسجيل الدخول",
      emailPlaceholder: "",
      passwordPlaceholder: "أدخل كلمة المرور",
    },
    dashboard: {
      title: "لوحة التحكم",
      description:
        "اعمل على الطلبات والمخزون والمنتجات والتحليلات ووسائل الدفع والسجلات الإدارية دون تغيير أي تدفقات خلفية.",
      overview: "نظرة عامة للإدارة",
      overviewTitle: "وحّد التجارة والمحتوى والعمليات في لوحة واحدة.",
      overviewBody:
        "تحافظ اللوحة على أدوات الطلبات والمخزون والمنتجات والمدفوعات والسجلات كما هي. يركّز هذا التحديث على الهيكل والتسلسل الهرمي وسلوك الجوال لتسهيل نفس العمليات بأمان.",
      shippingRates: "أسعار الشحن",
      shippingRatesBody: "عدّل الرسوم العامة وصفوف الأسعار الحية ومدخلات الحاسبة.",
      content: "المحتوى",
      contentBody: "حدّث نصوص الهيرو والبنرات وخطوات آلية العمل والإعلانات.",
      ordersQueue: "قائمة الطلبات",
      ordersQueueBody: "راجع الطلبات الواردة وحدّث الحالات وأضف الملاحظات وصدّر ملفات XLSX.",
      ordersQueueAction: "فتح الطلبات",
      catalogControl: "إدارة الكتالوج",
      catalogControlBody: "أدر طبقات المخزون وبطاقات المنتجات والرفع الجماعي وفحوصات البيانات الناقصة.",
      catalogControlAction: "فتح المنتجات",
      customers: "العملاء",
      products: "المنتجات",
      customersBody: "انتقل إلى منطقة العملاء المسجلين لمراجعة السجلات وعمليات إعادة التعيين.",
      customersAction: "فتح العملاء",
      shippingOps: "عمليات الشحن",
      shippingOpsBody: "عدّل الأسعار العامة ومدخلات الشحن فقط وبطاقات عناوين المستودعات.",
      shippingOpsAction: "فتح الشحن",
      workspace: "مساحة عمل الإدارة",
      workspaceNote: "تبقى الطلبات والمنتجات والمخزون ووسائل الدفع والسجلات والتحليلات ضمن مسار /admin الحالي.",
      orders: "الطلبات",
      orderCustomers: "عملاء الطلبات",
      trash: "المهملات",
      stock: "المخزون",
      shein: "SHEIN",
      missingData: "بيانات ناقصة",
      payments: "وسائل الدفع",
      logs: "السجلات",
      analytics: "التحليلات",
      allStatuses: "كل الحالات",
      exportXlsx: "تصدير XLSX",
      refresh: "تحديث",
      noOrdersYet: "لا توجد طلبات بعد",
      ordersHeading: "الطلبات",
      ordersBody: "أبقِ التنفيذ منظماً عبر تحديث الحالات والتصدير وملاحظات العملاء وإجماليات الدفع الواضحة.",
      customer: "العميل",
      paymentTotals: "الدفع والإجماليات",
      items: "العناصر",
      setStatus: "تعيين الحالة:",
      moveToTrash: "نقل إلى المهملات",
      cancellationReason: "سبب الإلغاء",
      searchByIdOrName: "ابحث بالرقم أو الاسم",
      noStockRecords: "لا توجد سجلات مخزون",
      single: "مفرد",
      customerSearchPlaceholder: "ابحث برقم العميل أو الهاتف أو البريد أو الاسم...",
      noCustomerMatch: "لا توجد نتيجة لـ \"{query}\"",
      noCustomersYet: "لا يوجد عملاء بعد",
      paymentMethods: "وسائل الدفع",
      paymentMethodsBody: "فعّل أو عطّل الوسائل التي يراها العملاء عند الدفع. تنطبق التغييرات فوراً على الموقع والتطبيق وبريد الطلب.",
      paymentLoadFailed: "تعذر تحميل وسائل الدفع",
      paymentAtLeastOne: "يجب أن تبقى وسيلة دفع واحدة فعّالة على الأقل.",
      enabled: "مفعّل",
      disabled: "معطّل",
      saveFailed: "فشل الحفظ",
      adminLogs: "سجلات الإدارة",
      adminLogsBody: "يتم تسجيل كل إجراء إداري مهم تلقائياً (سجل إضافي فقط). استخدم الفلاتر للوصول إلى التغييرات.",
      allKinds: "كل الأنواع",
      allSources: "كل المصادر",
      allSeverity: "كل مستويات الأهمية",
      allResults: "كل النتائج",
      logsActionPlaceholder: "صفِّ حسب الإجراء (مثل order.status أو product.update)...",
      noLogsYet: "لا توجد سجلات بعد",
      before: "قبل",
      after: "بعد",
      details: "التفاصيل",
      on: "على",
      by: "بواسطة",
      sheinCategories: "فئات SHEIN",
      addCategory: "إضافة فئة",
      sheinCategoriesBody: "اسحب الصفوف لإعادة الترتيب. الفئات المخفية تختفي من المتجر والتطبيق وتبقى مرتبطة بالمنتجات الحالية.",
      noCategoriesYet: "لا توجد فئات بعد",
      sheinLoadFailed: "تعذر تحميل فئات SHEIN",
      sheinDeleteConfirm: "حذف فئة SHEIN \"{name}\"؟ ستبقى المنتجات المرتبطة بها كما هي لكن ستختفي الشريحة من المتجر.",
      updated: "تم التحديث",
      created: "تم الإنشاء",
      deleted: "تم الحذف",
      deleteFailed: "فشل الحذف",
      orderSaved: "تم حفظ الترتيب",
      reorderFailed: "فشل إعادة الترتيب - حدّث الصفحة",
      show: "إظهار",
      hide: "إخفاء",
      edit: "تعديل",
      delete: "حذف",
      cancel: "إلغاء",
      save: "حفظ",
      create: "إنشاء",
      englishNameRequired: "الاسم بالإنجليزية مطلوب",
      editNamed: "تعديل \"{name}\"",
      newSheinCategory: "فئة SHEIN جديدة",
      idSlugOptional: "المعرّف (slug) - اختياري ويُنشأ تلقائياً من الاسم الإنجليزي",
      nameEn: "الاسم EN *",
      nameAr: "الاسم AR",
      nameFr: "الاسم FR",
    },
    analytics: {
      title: "التحليلات",
      description: "يفصل كل مؤشر أدناه بين الموقع وتطبيق الهاتف وغير المعروف والإجمالي.",
      notAvailable: "لا تتوفر تحليلات",
      today: "اليوم",
      last7Days: "آخر 7 أيام",
      last30Days: "آخر 30 يوماً",
      allTime: "كل الوقت",
      lastDays: "آخر {days} يوماً",
      all: "الكل",
      daysShort: "{days} ي",
      orders: "طلبات",
      revenue: "الإيراد",
      avg: "المتوسط",
      pageViews: "مشاهدات الصفحة",
      uniqueSessions: "جلسات فريدة",
      whatsappClicks: "نقرات واتساب",
      searches: "عمليات البحث",
      addToCart: "إضافة إلى السلة",
      failedCount: "{count} فشل",
      checkoutStarts: "بدء الدفع",
      successCount: "{count} ناجح",
      sourceTitle: "فصل المصدر: الموقع / التطبيق",
      sourceDescription: "الإجمالي يساوي دائماً الموقع + تطبيق الهاتف + غير المعروف، وتبقى البيانات القديمة بلا مصدر ظاهرة.",
      website: "الموقع",
      app: "التطبيق",
      mobileApp: "تطبيق الهاتف",
      unknown: "غير معروف",
      combined: "الإجمالي",
      total: "الإجمالي",
      appNotConnected: "تحليلات التطبيق غير مربوطة بعد.",
      visits: "الزيارات",
      events: "الأحداث",
      cartEvents: "أحداث السلة",
      checkoutEvents: "أحداث الدفع",
      warehouseViews: "مشاهدات المستودعات",
      shippingCalculatorViews: "مشاهدات حاسبة الشحن",
      authEvents: "أحداث الدخول / التسجيل",
      externalEvents: "سجلات تطبيق خارجية",
      customers: "العملاء",
      registered: "مسجلون",
      verified: "متحقق",
      unverified: "غير متحقق",
      withOrders: "لديهم طلبات",
      newIn30Days: "جدد خلال 30 يوماً",
      missingProfileData: "بيانات ملف ناقصة",
      products: "المنتجات",
      staticCatalog: "كتالوج ثابت",
      dbProducts: "منتجات قاعدة البيانات",
      overrideRows: "صفوف التجاوز",
      hidden: "مخفي",
      outOfStock: "نفد المخزون",
      orderRowsMissingIdentity: "صفوف طلب بهوية ناقصة",
      shipping: "الشحن",
      rates: "الأسعار",
      enabledRates: "أسعار مفعّلة",
      rateCountries: "بلدان الأسعار",
      rateMethods: "طرق الأسعار",
      shippingOnlyEntries: "مدخلات الشحن فقط",
      warehouses: "المستودعات",
      trackingHealth: "حالة التتبع",
      trackingNotAvailable: "بيانات التتبع غير متوفرة بعد.",
      rangeEvents: "أحداث النطاق",
      allTimeEvents: "كل الأحداث",
      lastEvent: "آخر حدث",
      range: "النطاق",
      collectionsFound: "المجموعات الموجودة",
      traffic: "الزيارات",
      noPageViews: "لا توجد مشاهدات صفحة بعد",
      languages: "اللغات",
      noLanguageData: "لا توجد بيانات لغة",
      devices: "الأجهزة",
      noDeviceData: "لا توجد بيانات أجهزة",
      countryPages: "صفحات البلدان",
      noCountryPageViews: "لا توجد مشاهدات لصفحات البلدان بعد",
      productInterest: "اهتمام المنتجات",
      viewed: "تمت المشاهدة",
      failedAdds: "إضافات فاشلة",
      topViewedProducts: "أكثر المنتجات مشاهدة",
      noProductViews: "لا توجد مشاهدات منتجات بعد",
      funnel: "قمع التحويل",
      productViews: "مشاهدات المنتجات",
      cartViews: "مشاهدات السلة",
      checkoutSuccess: "دفع ناجح",
      checkoutFailed: "دفع فاشل",
      missingStepData: "بيانات خطوة ناقصة",
      totalClicks: "إجمالي النقرات",
      topPages: "أهم الصفحات",
      noWhatsappClicks: "لا توجد نقرات واتساب بعد",
      byCountry: "حسب البلد",
      noCountryMetadata: "لا توجد بيانات بلد بعد",
      shippingTools: "أدوات الشحن",
      calculatorViews: "مشاهدات الحاسبة",
      calculations: "الحسابات",
      countrySelections: "اختيارات البلد",
      methodSelections: "اختيارات الطريقة",
      shippingOnlyOpens: "فتح الشحن فقط",
      addressCopies: "نسخ العنوان",
      shippingMarkCopies: "نسخ علامة الشحن",
      topMethods: "أكثر الطرق",
      noMethodSelections: "لا توجد اختيارات طريقة بعد",
      chinaSuppliersAuth: "موردو الصين + الدخول",
      chinaSuppliers: "موردو الصين",
      sectionViews: "مشاهدات القسم",
      openClicks: "نقرات الفتح",
      customerAuth: "دخول العملاء",
      loginSuccess: "دخول ناجح",
      loginFailed: "دخول فاشل",
      signupSuccess: "تسجيل ناجح",
      signupFailed: "تسجيل فاشل",
      verificationSent: "تم إرسال التحقق",
      verificationSuccess: "تحقق ناجح",
      passwordResetRequests: "طلبات إعادة كلمة المرور",
      recentEvents: "أحداث أخيرة",
      noTrackedEvents: "لا توجد أحداث متتبعة بعد",
      source: "المصدر",
      byStatusLifetime: "حسب الحالة (كل الوقت)",
      noOrders: "لا توجد طلبات",
      topProducts30d: "أفضل المنتجات (30 يوماً)",
      noDataYet: "لا توجد بيانات بعد",
      units: "وحدات",
      topCustomers: "أفضل العملاء",
      noCustomerOrderData: "لا توجد بيانات طلبات عملاء بعد",
      recentOrders: "طلبات أخيرة",
      noRecentOrders: "لا توجد طلبات أخيرة",
      recentAdminLogs: "سجلات إدارة أخيرة",
      noAdminLogs: "لا توجد سجلات إدارة بعد",
      totalLoggedActions: "إجمالي إجراءات الإدارة المسجلة",
    },
    customers: {
      title: "العملاء",
      description:
        "راجع سجلات العملاء المسجلين وهوية الحساب وحالة التحقق وإجراءات إرسال إعادة تعيين كلمة المرور دون كشف أي معلومات حساسة.",
      registeredEyebrow: "العملاء المسجلون",
      subscribers: "المشتركون",
      intro:
        "ابحث وافحص وأوقف وأرسل روابط إعادة التعيين الآمنة. تأتي أرقام العملاء وحقول الهوية الكاملة من سجل الخلفية عند توفرها.",
      total: "الإجمالي",
      active: "نشط",
      all: "الكل",
      inactive: "غير نشط",
      suspended: "موقوف",
      frozen: "مجمّد",
      deleted: "محذوف",
      closed: "مغلق",
      restrictedAccounts: "مجمّد / موقوف",
      searchPlaceholder: "ابحث برقم العميل أو الاسم أو البريد الإلكتروني أو الهاتف...",
      refresh: "تحديث",
      loadingCustomers: "جارٍ تحميل العملاء...",
      noCustomers: "لا يوجد عملاء يطابقون عوامل التصفية.",
      customerId: "رقم العميل",
      fullName: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      verification: "التحقق",
      verified: "متحقق",
      unverified: "غير متحقق",
      notAvailable: "غير متاح",
      status: "الحالة",
      orders: "الطلبات",
      totalSpent: "إجمالي الإنفاق",
      registered: "تاريخ التسجيل",
      customer: "العميل",
      date: "التاريخ",
      totalLabel: "الإجمالي",
      payment: "الدفع",
      detailTitle: "العميل",
      language: "اللغة",
      address: "العنوان",
      city: "المدينة",
      country: "البلد",
      deliveryNotes: "ملاحظات التسليم",
      lastLogin: "آخر تسجيل دخول",
      lastOrder: "آخر طلب",
      orderCount: "عدد الطلبات",
      adminActions: "إجراءات الإدارة",
      reactivate: "إعادة التفعيل",
      freeze: "تجميد",
      suspend: "إيقاف",
      markInactive: "تعيين كغير نشط",
      activate: "نشط",
      deleteConfirm: "حذف حساب هذا العميل؟ يتم الاحتفاظ بالطلبات.",
      deleteSuccess: "تم حذف حساب العميل",
      sendResetEmail: "إرسال بريد إعادة تعيين كلمة المرور",
      sendResetPrompt: "إرسال رابط إعادة التعيين إلى {email}؟",
      yesSend: "نعم، أرسل",
      cancel: "إلغاء",
      resetNotice: "روابط إعادة التعيين تُستخدم مرة واحدة، وتنتهي خلال 60 دقيقة، وتُرسل عبر البريد الإلكتروني. لا يتم عرض الرمز في لوحة الإدارة.",
      recentOrders: "الطلبات الأخيرة",
      lastLoginNote: "لا تعيد هذه الواجهة حالياً حقلاً تدقيقياً لآخر تسجيل دخول لكل عميل.",
      noOrdersLinked: "لا توجد طلبات مرتبطة بهذا العميل.",
      deletionRequests: "طلبات حذف الحساب",
      deletionQueue: "قائمة المراجعة المعلّقة",
      deletionIntro:
        "راجع طلبات إغلاق حسابات العملاء هنا. الحذف يغلق الحساب مع الحفاظ على سجل الطلبات، والرفض يبقي الحساب نشطاً. تتم إدارة التجميد وفك التجميد بشكل مستقل من تفاصيل العميل.",
      loadingDeletionRequests: "جارٍ تحميل طلبات الحذف...",
      noDeletionRequests: "لا توجد طلبات حذف حساب.",
      reason: "السبب",
      reasonOther: "أخرى",
      requested: "تاريخ الطلب",
      note: "ملاحظة",
      adminDecisionNote: "ملاحظة قرار الإدارة",
      rejectionReason: "سبب الرفض",
      rejectionReasonPlaceholder: "مطلوب عند رفض الطلب",
      rejectionReasonRequired: "يرجى كتابة سبب الرفض قبل رفض الطلب.",
      markReviewed: "تحديد كمراجَع",
      markCompleted: "تحديد كمكتمل",
      deleteAccountAction: "حذف الحساب",
      freezeAccountAction: "تجميد الحساب",
      unfreezeAccountAction: "فك تجميد الحساب",
      reject: "رفض",
      internalId: "معرّف داخلي",
      deletionSections: {
        pending: "طلبات قيد المراجعة",
        frozen: "حسابات مجمّدة",
        completed: "حسابات محذوفة",
        rejected: "طلبات مرفوضة",
      },
      notifications: "إشعارات التطبيق",
      verifiedSendControl: "إرسال بعد التحقق",
      accountSource: "مصدر الحساب",
      sourceCustomer: "عميل",
      sourceLegacyUser: "مستخدم قديم",
      sourceUnknown: "غير معروف",
      deletionStatusUpdated: "تم تحديث طلب الحذف",
      accountRemainsFrozen: "تم رفض الطلب، لكن الحساب بقي مجمداً لأنه جُمّد لسبب آخر.",
      verificationCodeSent: "تم إرسال رمز التحقق إلى {email}",
      pushIntro: "حضّر إشعاراً، اطلب رمز تحقق من المدير عبر البريد، ثم حاول الإرسال النهائي. هذا الإصدار لا يزيّف التسليم إذا لم تكن رموز التطبيق أو المزود مهيأة.",
      pushWarning: "سيتم إرسال هذه الرسالة إلى مستخدمي التطبيق. تأكد من المحتوى جيداً قبل الإرسال.",
      pushWarningAr: "سيتم إرسال هذه الرسالة إلى مستخدمي التطبيق. تأكد من المحتوى جيداً قبل الإرسال.",
      notificationTitle: "عنوان الإشعار",
      notificationBody: "نص الإشعار",
      audience: "الجمهور",
      allUsers: "كل المستخدمين",
      verificationCode: "رمز التحقق",
      requestVerificationCode: "طلب رمز التحقق",
      verifyAndSend: "تحقق وإرسال",
      preview: "معاينة",
      notificationTitleFallback: "عنوان الإشعار",
      notificationBodyFallback: "ستظهر معاينة الإشعار هنا قبل طلب رمز التحقق.",
      recentAttempts: "محاولات أخيرة",
      recentAttemptsBody: "يظهر كل طلب رمز وكل محاولة إرسال هنا.",
      loadingNotificationLogs: "جارٍ تحميل سجلات الإشعارات...",
      noNotificationAttempts: "لا توجد محاولات إشعار مسجلة بعد.",
      provider: "المزود",
      loadingCustomer: "جارٍ تحميل العميل...",
      verifiedAt: "تم التحقق",
      requestStatus: {
        pending: "معلّق",
        reviewed: "تمت المراجعة",
        completed: "مكتمل",
        frozen: "مجمّد",
        deleted: "محذوف",
        rejected: "مرفوض",
        cancelled: "ملغى",
        failed: "فشل",
        code_requested: "تم طلب الرمز",
        draft: "مسودة",
      },
    },
    shipping: {
      title: "أسعار الشحن",
      description:
        "أدر رسوم التوصيل العامة وتلميحات سعر الصرف ومصفوفة الأسعار المباشرة حسب البلد والطريقة المستخدمة عبر أسطح الشحن الموجهة للعملاء.",
      globalFees: "الرسوم العامة",
      deliveryFee: "رسوم التوصيل (دولار)",
      exchangeRate: "سعر صرف الدولار إلى الليرة",
      deliveryFeeHint: "تُحتسب على كل طلب من متجر BOB",
      exchangeRateHint: "تُستخدم لإظهار تلميحات الأسعار بالليرة",
      tableTitle: "أسعار الشحن حسب البلد والطريقة",
      addRate: "إضافة سعر",
      noRates: "لا توجد أسعار بعد. اضغط على إضافة سعر للبدء.",
      saveChanges: "حفظ التغييرات",
      country: "البلد",
      method: "الطريقة",
      productCategory: "فئة المنتج",
      price: "السعر",
      unit: "الوحدة",
      combo: "+ إضافة",
      eta: "مدة الوصول",
      active: "نشط",
      id: "المعرّف",
    },
    shippingOnly: {
      title: "الشحن فقط",
      description:
        "حرّر نصوص الخدمة وتعليمات SHEIN وقائمة الأسعار المخصصة للشحن فقط المستخدمة على الموقع والتطبيق.",
      about: "حول هذا القسم",
      aboutBody:
        "الشحن فقط خدمة مستقلة عن حاسبة الشحن من البلد إلى لبنان. التعديلات هنا تؤثر فقط على صفحة /shipping-only العامة والتطبيق.",
      prices: "الأسعار",
      pageTexts: "نصوص الصفحة",
      sheinInstructions: "تعليمات SHEIN",
      warehousesLink: "المستودعات",
      addPrice: "إضافة سعر",
      saveChanges: "حفظ التغييرات",
      loading: "جارٍ التحميل...",
      publicPageTexts: "نصوص الصفحة العامة",
      warehousesIntro:
        "تُدار عناوين المستودعات وأرقام الهواتف وعلامات الشحن والملاحظات في قسم مستقل. تنعكس التعديلات هناك تلقائياً على صفحة /shipping-only العامة.",
      openWarehouses: "فتح المستودعات",
    },
    warehouses: {
      title: "المستودعات",
      description:
        "أدر كل بطاقة مستودع وخط تواصل وعلامة شحن وكتلة عنوان تغذي الموقع والتطبيق وصفحات الشحن فقط.",
      subtitle: "مصدر موحد للبيانات - يطابق صفحة /shipping-only والموقع والتطبيق.",
      newWarehouse: "مستودع جديد",
      noWarehouses: "لا توجد مستودعات بعد. اضغط على مستودع جديد لإضافة واحد.",
      active: "نشط",
      hidden: "مخفي",
      edit: "تعديل",
      create: "إنشاء",
      save: "حفظ",
      cancel: "إلغاء",
      idSlug: "المعرّف (slug)",
      city: "المدينة",
      shippingMethod: "طريقة الشحن",
      deliveryEta: "مدة التسليم",
      shippingMark: "علامة الشحن",
      warehouseCode: "رمز المستودع (داخلي)",
      sortOrder: "ترتيب الظهور",
      mapUrl: "رابط الخريطة (اختياري)",
      email: "البريد الإلكتروني",
      whatsapp: "واتساب (خاص بالمستودع)",
      contactLabel: "اسم جهة التواصل / الوصف",
      chineseAddress: "العنوان الصيني",
      phoneNumbers: "أرقام الهاتف",
      addPhone: "+ إضافة",
      noPhoneNumbers: "لا توجد أرقام هاتف بعد.",
    },
    content: {
      title: "المحتوى",
      description:
        "أدر صفوف تغذية المحتوى المتصلة بالموقع والتطبيق. تبقى إعدادات المنتجات والطلبات والمستودعات والشحن والحسابات في أقسامها المخصصة.",
      heroEntries: "عناصر الهيرو",
      banners: "البنرات",
      steps: "خطوات \"كيف تعمل الخدمة\"",
      announcements: "الإعلانات",
      addHero: "إضافة هيرو",
      addBanner: "إضافة بنر",
      addStep: "إضافة خطوة",
      addAnnouncement: "إضافة إعلان",
      saveAll: "حفظ الكل",
      loading: "جارٍ التحميل...",
      failedToLoad: "فشل تحميل المحتوى",
      saved: "تم حفظ المحتوى",
      saveFailed: "فشل الحفظ",
      heroHint: "أنشئ عنصراً لكل نسخة منصة - وحدد Web أو Mobile أو Both.",
      advancedFeedTitle: "تغذية المحتوى المتقدمة",
      advancedFeedBody: "تم إخفاء أدوات تغذية الصفحة الرئيسية لأنها لا تحتوي على صفوف مباشرة حالياً. تبقى إعدادات الشحن والمستودعات والمنتجات والحسابات في أقسامها المخصصة. افتح هذا الجزء فقط عند الحاجة لإنشاء صفوف تغذية للموقع أو التطبيق.",
      showFeedControls: "إظهار أدوات التغذية",
      heroItem: "عنصر هيرو",
      bannerItem: "بنر",
      stepItem: "خطوة",
      announcementItem: "إعلان",
      titleLabel: "العنوان",
      subtitleLabel: "النص الفرعي",
      descriptionLabel: "الوصف",
      textLabel: "النص",
      iconName: "اسم الأيقونة",
      visible: "ظاهر",
      image: "الصورة",
      linkUrl: "رابط الانتقال",
      type: "النوع",
      noHeroEntries: "لا توجد عناصر هيرو بعد.",
      noBanners: "لا توجد بنرات بعد.",
      noSteps: "لا توجد خطوات بعد.",
      noAnnouncements: "لا توجد إعلانات بعد.",
      workflow: "مسودة / معاينة / نشر",
      status: "الحالة",
      statuses: {
        draft: "مسودة",
        published: "منشور",
      },
      lastSaved: "آخر حفظ",
      notSaved: "لم يتم الحفظ",
      lastPublished: "آخر نشر",
      notPublished: "لم يتم النشر",
      appNotConnected: "محتوى التطبيق غير متصل بعد.",
      all: "الكل",
      website: "الموقع",
      app: "التطبيق",
      preview: "معاينة",
      publish: "نشر",
      hero: "هيرو",
      banner: "بنر",
      step: "خطوة",
      announcement: "إعلان",
      untitled: "بدون عنوان",
      noPreviewItems: "لا توجد عناصر معاينة لهذه الواجهة.",
      draftSaved: "تم حفظ المسودة",
      publishFailed: "فشل النشر",
      published: "تم نشر المحتوى",
      saveDraft: "حفظ المسودة",
    },
    settings: {
      title: "الإعدادات",
      description: "إدارة أدوار الإدارة وفصل المصادر وأمان الدخول وأدوات الحساب لإدارة الموقع.",
      accountControls: "أدوات الحساب",
      changePasswordHint: "غيّر كلمة مرور حسابك الإداري.",
      role: "الدور",
      pendingApprovals: "الموافقات المعلقة",
      unreadNotifications: "الإشعارات غير المقروءة",
      lockedLogins: "حسابات دخول مقفلة",
      sourcePolicy: "سياسة المصدر والمزامنة",
      websiteCustomers: "عملاء الموقع",
      mobileApp: "التطبيق",
      contentWorkflow: "سير عمل المحتوى",
      customerIds: "أرقام العملاء",
      employeeAccounts: "حسابات الموظفين والإدارة",
      employeeDomainHint: "يجب أن ينتهي بريد الموظف بـ @bobserviceslb.com.",
      name: "الاسم",
      email: "البريد الإلكتروني",
      temporaryPassword: "كلمة مرور مؤقتة",
      createAccount: "إنشاء حساب",
      currentStaff: "الموظفون الحاليون",
      resetPassword: "إعادة تعيين كلمة المرور",
      rolePermissions: "صلاحيات الأدوار",
      permissions: "الصلاحيات",
      presets: "قوالب جاهزة",
      pendingApprovalsWarning: "{count} طلب موافقة يحتاج إلى مراجعة.",
      lockedLoginsWarning: "{count} حساب دخول مقفل مؤقتاً.",
    },
    approvals: {
      title: "الموافقات",
      description: "راجع التغييرات الإدارية الحساسة قبل اعتمادها كطلبات تشغيلية موافق عليها.",
      newRequest: "طلب جديد",
      requestTitle: "العنوان",
      area: "القسم",
      targetId: "رقم الهدف",
      requestDescription: "الوصف",
      createRequest: "إنشاء الطلب",
      requests: "الطلبات",
      loading: "جارٍ تحميل طلبات الموافقة...",
      empty: "لا توجد طلبات موافقة بعد.",
      requestedBy: "طلب بواسطة",
      reviewedBy: "تمت المراجعة بواسطة",
      reviewedAt: "وقت المراجعة",
      note: "ملاحظة",
      approve: "موافقة",
      reject: "رفض",
      created: "تم إنشاء طلب الموافقة",
      approved: "تمت الموافقة",
      rejected: "تم الرفض",
      loadFailed: "تعذر تحميل طلبات الموافقة",
      createFailed: "فشل الإنشاء",
      decisionFailed: "فشل القرار",
      decisionNotePrompt: "ملاحظة قرار اختيارية",
    },
    notifications: {
      title: "الإشعارات",
      description: "تنبيهات الأمان والموافقات والمحتوى والنظام الناتجة عن نشاط إدارة الموقع.",
      all: "الكل",
      unreadCount: "غير مقروء ({count})",
      readAll: "قراءة الكل",
      loading: "جارٍ تحميل الإشعارات...",
      empty: "لا توجد إشعارات بعد.",
      markRead: "وضع كمقروء",
      markedRead: "تم وضع الإشعارات كمقروءة",
      loadFailed: "تعذر تحميل الإشعارات",
      markFailed: "تعذر وضع الإشعار كمقروء",
      markAllFailed: "تعذر وضع كل الإشعارات كمقروءة",
    },
  },
};

function readStoredLanguage() {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "ar" ? "ar" : "en";
}

function resolvePath(source, path) {
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), source);
}

export function AdminI18nProvider({ children }) {
  const [lang, setLang] = useState(readStoredLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const value = useMemo(() => {
    const table = translations[lang] || translations.en;
    return {
      lang,
      setLang,
      isRTL: lang === "ar",
      dir: lang === "ar" ? "rtl" : "ltr",
      t: (key, fallback = key) => resolvePath(table, key) ?? resolvePath(translations.en, key) ?? fallback,
    };
  }, [lang]);

  return <AdminI18nContext.Provider value={value}>{children}</AdminI18nContext.Provider>;
}

export function useAdminI18n() {
  const value = useContext(AdminI18nContext);
  if (!value) throw new Error("useAdminI18n must be used inside AdminI18nProvider");
  return value;
}
